import { Badge, Banner, Button, Dialog } from '@cloudflare/kumo';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ICONS } from './ui/icons';
import { useAuthenticatedFetch } from '../hooks/useAuthenticatedFetch';
import { useAppToast } from '../hooks/useAppToast';

export interface MetaFlowRow {
  id: string; flowId: string; name: string; status: string; categories: string[]; fields: string[];
  fieldMappings: Record<string, string>; completedCount: number; syncedAt: string;
}
const STATUS: Record<string, { label: string; color: 'green' | 'orange' | 'neutral' | 'red' }> = {
  PUBLISHED: { label: 'Publicado', color: 'green' }, DRAFT: { label: 'Rascunho', color: 'orange' },
  DEPRECATED: { label: 'Descontinuado', color: 'neutral' }, BLOCKED: { label: 'Bloqueado', color: 'red' }, THROTTLED: { label: 'Limitado', color: 'orange' },
};

/** API oficial › Formulários (WhatsApp Flows) de uma conexão: sincronizar e ligar os campos */
export function MetaFlowsModal({ sessionName, label, canEdit, onClose }: { sessionName: string; label: string; canEdit: boolean; onClose: () => void }) {
  const fetchApi = useAuthenticatedFetch();
  const toast = useAppToast();
  const [flows, setFlows] = useState<MetaFlowRow[] | null>(null);
  const [fields, setFields] = useState<Array<{ key: string; name: string }>>([]);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    const [f, cf] = await Promise.all([
      fetchApi(`/api/tools/api-oficial/${encodeURIComponent(sessionName)}/flows`).then((r) => (r.ok ? r.json() : { flows: [] })),
      fetchApi('/api/custom-fields').then((r) => (r.ok ? r.json() : { fields: [] })),
    ]);
    setFlows(f.flows || []);
    setFields(cf.fields || []);
  }, [fetchApi, sessionName]);
  useEffect(() => { load(); }, [load]);

  const sync = async () => {
    setSyncing(true);
    const res = await fetchApi(`/api/tools/api-oficial/${encodeURIComponent(sessionName)}/flows/sync`, { method: 'POST' });
    const j = await res.json().catch(() => ({}));
    setSyncing(false);
    if (!res.ok) { toast.error(j.message || 'Não foi possível buscar os formulários'); return; }
    setFlows(j.flows);
    toast.success(`${j.flows.length} formulário${j.flows.length === 1 ? '' : 's'} na conta`);
  };

  const map = async (flow: MetaFlowRow, param: string, key: string) => {
    const mappings = { ...flow.fieldMappings, [param]: key };
    if (!key) delete mappings[param];
    const res = await fetchApi(`/api/tools/api-oficial/flows/${flow.id}/mappings`, { method: 'PUT', body: JSON.stringify({ mappings }) });
    const j = await res.json().catch(() => ({}));
    if (!res.ok) { toast.error(j.message || 'Não foi possível salvar'); return; }
    setFlows((fs) => fs?.map((f) => (f.id === flow.id ? j.flow : f)) ?? null);
  };

  return (
    <Dialog.Root open={true} onOpenChange={(o: boolean) => { if (!o) (onClose)(); }}>
<Dialog size="lg" className="p-6">


      <div className="space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold">Formulários (WhatsApp Flows) · {label}</h3>
            <div style={{ color: 'var(--text-muted)' }}>
              Crie o formulário no Gerenciador do WhatsApp (Ferramentas da conta › Flows), publique e busque aqui. Envie pelas sequências;
              as respostas vão para os <Link to="/campos">campos personalizados</Link> do contato. Campo do formulário com o mesmo nome da chave
              de um campo personalizado é ligado sozinho.
            </div>
          </div>
          {canEdit && <Button variant="secondary" icon={ICONS.Refresh} loading={syncing} onClick={sync}>Buscar na Meta</Button>}
        </div>
        {flows && flows.length === 0 && <Banner variant="default" description={<>Nenhum formulário sincronizado ainda.</>} />}
        {flows?.map((f) => {
          const st = STATUS[f.status] ?? { label: f.status, color: 'neutral' as const };
          return (
            <div key={f.id} className="brand-card space-y-2">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <strong>{f.name}</strong>
                  <Badge variant={st.color}>{st.label}</Badge>
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{f.completedCount} resposta{f.completedCount === 1 ? '' : 's'}</div>
              </div>
              {f.fields.length === 0 && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Sem campos de formulário encontrados.</div>}
              {f.fields.map((p) => (
                <div key={p} className="flex items-center gap-3" style={{ fontSize: 13 }}>
                  <code style={{ minWidth: 160 }}>{p}</code>
                  <ICONS.ArrowRight size={16} />
                  <select aria-label={`Campo para ${p}`} disabled={!canEdit} value={f.fieldMappings[p] ?? (fields.some((x) => x.key === p) ? p : '')}
                    onChange={(e) => map(f, p, e.target.value)}
                    style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid var(--border-strong)', fontSize: 13, fontFamily: 'inherit', minWidth: 220 }}>
                    <option value="">Não guardar</option>
                    {fields.map((x) => <option key={x.key} value={x.key}>{x.name}</option>)}
                  </select>
                </div>
              ))}
            </div>
          );
        })}
        <div className="flex justify-end"><Button variant="secondary" onClick={onClose}>Fechar</Button></div>
      </div>
    
</Dialog>
</Dialog.Root>
  );
}
